import forEach from 'lodash/forEach';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import flatten from 'lodash/flatten';
import assign from 'lodash/assign';
import rison from 'rison';

// This function checks for a difference between the title and the description
// for all sub-tags within the supplied tag category. Often used to determine
// if the Show Full Text button should be shown.
export function recursiveTagDescDelta(tag) {
  if (tag.tags && tag.tags.length) {
    const deepTag = tag.tags.map(recursiveTagDescDelta);
    if (isArray(deepTag) && deepTag.indexOf(true) > -1) {
      return true;
    }
  }
  if (tag.description && tag.title !== tag.description) {
    return true;
  }
  return false;
}

export const determineFirstLevel = (tagCategory) => {
  if (tagCategory.tags !== undefined && tagCategory.tags.length === 1) {
    if (tagCategory.tags[0].tags && tagCategory.tags[0].tags.length > 0) {
      return tagCategory.tags[0];
    }
    return tagCategory;
  } else if (tagCategory.tags !== undefined) {
    return tagCategory;
  }
  return null;
};

/*
 * An array with all ids of all sub-children and children of current node.
 * It includes the id of current node too
 */
export const getTagChildrenIds = (tag) => [
  tag.id,
  ...flatten((tag.tags || []).map(getTagChildrenIds)),
];

export const flatHierarchy = (
  tagsHierarchy = [],
  hierarchyRef,
  tagCategoryId,
) => {
  tagsHierarchy.forEach((tagHierarchy) => {
    const { id, parent_id: parentId, tags } = tagHierarchy;
    const hasChildren = tags && tags.length;
    if (parentId) {
      hierarchyRef.parentByChild[id] = parentId;

      if (isEmpty(hierarchyRef[parentId])) {
        hierarchyRef[parentId] = {
          id: parentId,
          tagCategoryId,
          isSelected: false,
          childrenIds: [parentId],
        };
      }

      // Push each sub-children of each child of the current `parentId` inspected
      hierarchyRef[parentId].childrenIds.push(
        ...getTagChildrenIds(tagHierarchy),
      );

      hierarchyRef[parentId].parentId = hierarchyRef.parentByChild[parentId];
      if (!hierarchyRef[parentId].children) {
        hierarchyRef[parentId].children = {};
      }
      hierarchyRef[parentId].children[id] = {
        id,
        parentId,
        isSelected: false,
      };
    } else {
      hierarchyRef[id] = {
        id,
        parentId: null,
        isSelected: false,
        childrenIds: getTagChildrenIds(tagHierarchy),
      };
    }

    if (hasChildren) {
      flatHierarchy(tags, hierarchyRef, tagCategoryId);
    }
  });

  return hierarchyRef;
};

const getTagsSelectedStatus = (tags) => {
  let isSelected = true;
  if (isEmpty(tags)) {
    return false;
  }
  forEach(tags, (child) => {
    if (!child.isSelected) {
      isSelected = false;
      return false;
    }
  });

  return isSelected;
};

const setParentSelections = (
  action,
  tagId,
  parentId,
  hierarchyRef,
  tagsById,
) => {
  if (hierarchyRef[parentId]) {
    hierarchyRef[parentId].children[tagId].isSelected =
      action === 'select' &&
      getTagsSelectedStatus(hierarchyRef[tagId].children);

    const isSelected = getTagsSelectedStatus(hierarchyRef[parentId].children);
    hierarchyRef[parentId].isSelected =
      isSelected || hierarchyRef[parentId].isClicked;

    const grandParent = tagsById[parentId];
    if (grandParent) {
      setParentSelections(
        grandParent.id,
        grandParent.parent_id,
        hierarchyRef,
        tagsById,
      );
    }
  }
};

export const selectTag = (tagId, parentId, hierarchyRef, tagsById) => {
  if (hierarchyRef[tagId]) {
    hierarchyRef[tagId].isClicked = true;
    hierarchyRef[tagId].isSelected = true;
  }
  if (hierarchyRef[parentId]) {
    hierarchyRef[parentId].children[tagId].isSelected = true;

    const isSelected = getTagsSelectedStatus(hierarchyRef[parentId].children);
    hierarchyRef[parentId].isSelected = isSelected;

    const grandParent = tagsById[parentId];
    if (grandParent) {
      setParentSelections(
        'select',
        grandParent.id,
        grandParent.parent_id,
        hierarchyRef,
        tagsById,
      );
    }
  }
};

export const deselectTag = (tagId, parentId, hierarchyRef, tagsById) => {
  if (hierarchyRef[tagId]) {
    hierarchyRef[tagId].isClicked = false;
    const areChildrenSelected = getTagsSelectedStatus(
      hierarchyRef[tagId].children,
    );
    hierarchyRef[tagId].isSelected = areChildrenSelected;
  }

  if (hierarchyRef[parentId]) {
    hierarchyRef[parentId].children[tagId].isSelected = false;
    const areChildrenSelected = getTagsSelectedStatus(
      hierarchyRef[parentId].children,
    );
    hierarchyRef[parentId].isSelected =
      hierarchyRef[parentId].isClicked || areChildrenSelected;
  }

  const grandParent = tagsById[parentId];
  if (grandParent) {
    setParentSelections(
      'deselect',
      grandParent.id,
      grandParent.parent_id,
      hierarchyRef,
      tagsById,
    );
  }
};

export const setTagSelections = (type, tags, hierarchyRef, tagsById) => {
  const isSelected = type === 'select';
  tags.forEach(({ tags: childrenTags, id: tagId, parent_id: parentId }) => {
    if (hierarchyRef[parentId]) {
      hierarchyRef[parentId].isSelected = isSelected;
      if (hierarchyRef[parentId].children[tagId].isSelected) {
        if (type === 'select') {
          selectTag(tagId, parentId, hierarchyRef, tagsById);
        } else {
          deselectTag(tagId, parentId, hierarchyRef, tagsById);
        }
      }
    } else if (!parentId) {
      hierarchyRef[tagId].isSelected = isSelected;
    }

    const hasTags = childrenTags && childrenTags.length > 0;
    if (hasTags) {
      setTagSelections(type, childrenTags, hierarchyRef, tagsById);
    }
  });
};

export const recursiveTagIndex = (tag) => {
  if (tag) {
    if (tag.tags) {
      return assign({ [tag.id]: tag }, ...tag.tags.map(recursiveTagIndex));
    }
    return { [tag.id]: tag };
  }
};

export const preToggleFilters = (selectedFilters, hierarchyRef, tagsById) => {
  selectedFilters.forEach((id) => {
    const { id: tagId, parent_id: parentId } = tagsById[id] || {};

    selectTag(tagId, parentId, hierarchyRef, tagsById);
  });
};

export const createFlattenHierarchyData = (
  categories,
  hierarchyRef,
  filters,
) => {
  hierarchyRef.parentByChild = {};
  hierarchyRef.tree = {};

  const filtersByCategory = rison.decode_object(filters);

  const { above_fold: aboveFold, below_fold: belowFold } = categories;
  let tagsById = {};
  aboveFold.concat(belowFold).forEach((tagCategory) => {
    const { id: tagCategoryId } = tagCategory;
    const firstLevel = determineFirstLevel(tagCategory);
    hierarchyRef.tree[tagCategoryId] = firstLevel;
    flatHierarchy(firstLevel.tags, hierarchyRef, tagCategoryId);

    tagsById = {
      ...tagsById,
      ...recursiveTagIndex(tagCategory),
    };
  });

  /**
   * Backwards compatible
   * */
  hierarchyRef.filtersByCategory = filtersByCategory;
  forEach(filtersByCategory, (selectedFilters) => {
    preToggleFilters(selectedFilters, hierarchyRef, tagsById);
  });
};

export const extractTags = (items) => {
  if (items.tags && items.tags.length === 1) {
    return extractTags(items.tags[0]);
  }
  return items.tags;
};

/**
 * Gets tags from the PPC results related questions
 *
 * @param {*} categories
 * @param {*} tagId
 * @returns
 * @memberof CreateQuiz
 */

export const findTagById = (categories, tagId) => {
  const allCategories = categories.above_fold.concat(categories.below_fold);

  const processTags = (category) => {
    let found = null;
    const { tags, ...rest } = category;
    const stack = [rest, ...tags];

    while (stack.length) {
      const next = stack.pop();
      const { tags: innerTags, ...parentInfo } = next;
      if (Array.isArray(innerTags) && innerTags.length > 0) {
        stack.push(...innerTags);
        if (parentInfo.id === Number.parseInt(tagId, 10)) {
          found = parentInfo;
          break;
        }
      } else if (next.id === Number.parseInt(tagId, 10)) {
        found = next;
        break;
      }
    }
    return found;
  };

  const category = allCategories.find((cat) => {
    const tag = processTags(cat, tagId);
    return tag !== null;
  });

  if (category) {
    return `'${category.id}':!(${tagId})`;
  }
  return '';
};
